import { SelectItemBoolean } from "../../SharedModule/types/shared-module-types";
import {
  Client,
  ContactProfile,
  AllDropdownsInfo,
  NewContactProfile,
  DropdownsInfo,
  NewClient,
  ClientsKeyContactDetail,
  ClientKeyContactDetailForm,
  StakeholderConfidence,
  AdvocacyLevel,
  ClientRecurringBillingItem,
  ClientWorkOrderItem,
  ClientProjectItem,
} from "../types/clients-module-types";

// generate array for client owner dropdown in clients list page
export const generateDistinctOwners = (arr) => {
  const seen = new Set();

  return arr.reduce((acc, company: Client) => {
    if (company.companyOwnerUserId && !seen.has(company.companyOwnerUserId)) {
      seen.add(company.companyOwnerUserId);
      acc.push({
        label: company.companyOwnerName,
        value: company.companyOwnerUserId,
      });
    }
    return acc;
  }, []);
};

export const CONTACT: ContactProfile = {
  assistantEmail: "",
  assistantFirstName: "",
  assistantLastName: "",
  assistantPhoneNumber: "",
  city: "",
  clients: [],
  contactRoleType: "",
  contactRoleTypeId: [],
  country: "",
  countryId: null,
  dateOfBirth: null,
  department: "",
  discProfile: "",
  discProfileOptionId: null,
  email: "",
  firstName: "",
  hobbies: "",
  id: 0,
  isActive: true,
  jobTitle: "",
  lastName: "",
  lastUpdateBy: "",
  lastUpdateDate: undefined,
  mobilePhoneNumber: "",
  nickname: "",
  phoneNumber: "",
  postalCode: "",
  salutation: "",
  salutationId: null,
  spouseFirstName: "",
  spouseLastName: "",
  state: "",
  stateId: null,
  streetAddress: "",
  streetAddress2: "",
};

export const NEW_CONTACT: NewContactProfile = {
  assistantEmail: "",
  assistantFirstName: "",
  assistantLastName: "",
  assistantPhoneNumber: "",
  city: "",
  clientIds: [],
  contactRoleTypeId: [],
  countryId: null,
  dateOfBirth: null,
  department: "",
  discProfileOptionId: null,
  email: "",
  firstName: "",
  hobbies: "",
  id: null,
  isActive: true,
  jobTitle: "",
  lastName: "",
  mobilePhoneNumber: "",
  nickname: "",
  phoneNumber: "",
  postalCode: "",
  salutationId: null,
  spouseFirstName: "",
  spouseLastName: "",
  stateId: null,
  streetAddress: "",
  streetAddress2: "",
};

export const isValidEmailFormat = (email) => {
  const regex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
  return regex.test(email);
};

const setDropdownWithIdName = (array) => {
  return array
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((elem) => ({
      value: elem.id,
      label: elem.name,
    }));
};

const setExternalDropdown = (array, isExternal = true) => {
  return array
    ? setDropdownWithIdName(
        array.filter((elem) => elem.isExternal === isExternal)
      )
    : [];
};

export const loadClientProfileDropdownInfo = (
  structure: AllDropdownsInfo
): DropdownsInfo => {
  const partial = {
    advocacyLevelOptions: setExternalDropdown(structure?.advocacyLevelOptions),
    billingCycles: setExternalDropdown(structure?.billingCycles),
    billingTerms: setExternalDropdown(structure?.billingTerms),
    concernStatuses: setExternalDropdown(structure?.concernStatuses),
    contactRoleTypes: setExternalDropdown(structure?.contactRoleTypes),
    countries: setExternalDropdown(structure?.countries, false),
    customMasterAgreements: setExternalDropdown(
      structure?.customMasterAgreements
    ),
    customNdas: setExternalDropdown(structure?.customNdas),
    discProfiles: setExternalDropdown(structure?.discProfiles),
    industryTypes: setExternalDropdown(structure?.industryTypes),
    invoiceFormats: setExternalDropdown(structure?.invoiceFormats),
    leadGenerationSourceTypes: setExternalDropdown(
      structure?.leadGenerationSourceTypes
    ),
    referralTypes: setExternalDropdown(structure?.referralTypes),
    relationshipLevelTrends: setExternalDropdown(
      structure?.relationshipLevelTrends
    ),
    relationshipTypes: setExternalDropdown(structure?.relationshipTypes),
    salutations: setExternalDropdown(structure?.salutations),
    sourceTypes: setExternalDropdown(structure?.sourceTypes),
    states: setExternalDropdown(structure?.states, false),
    taxJurisdictions: setExternalDropdown(structure?.taxJurisdictions),
  };
  return partial;
};

// need string values because some ids could be different in stg/release/prod
export const NEW_CLIENT: NewClient = {
  accountingNotes: null,
  billingCycleId: 0,
  billingCycleName: "Internal",
  billingTermId: 0,
  billingTermName: "Net 15",
  city: "Rochester",
  companyAliasName: null,
  companyDomainName: null,
  companyName: "",
  companyOwnerUserId: "",
  comprehensiveCareClient: null,
  continuousInnovationClient: null,
  countryId: 0,
  countryName: "UNITED STATES",
  currentClient: true,
  customNdaId: 0,
  customMasterAgreement: 0,
  industryTypeId: 0,
  industryTypeName: "Business Services",
  invoiceFormatId: 0,
  invoiceFormatName: "Detail",
  leadGenerationSourceTypeIds: [],
  legalInformationLocation: null,
  legalNotes: null,
  phoneNumber: null,
  postalCode: "14604",
  priceSchedule: 5,
  purchaseOrderRequired: null,
  referredBy: null,
  relationshipTypeId: 0,
  relationshipTypeName: "Internal",
  sourceTypeId: 0,
  sourceTypeName: "Internal",
  stateId: 0,
  stateName: "New York",
  streetAddress: "1 S Clinton Ave",
  streetAddress2: null,
  taxJurisdictionId: 0,
  taxJurisdictionName: "NY - Monroe County",
  taxable: false,
  useAuthorization: null,
  websiteUrl: null,
};

export const YES_NO_OPTIONS: SelectItemBoolean[] = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

// structure with cc and childrens
export const getAllCostCentersIds = (data) => {
  let ids: any[] = [];

  function traverse(node: any) {
    ids.push(node.id);
    if (Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  data.forEach(traverse);
  return ids;
};

export const getBillPeriodText = (value: number) => {
  switch (value) {
    case 1:
      return "Monthly";

    case 3:
      return "Every 3 months";

    case 4:
      return "Every 4 months";

    case 6:
      return "Every 5 months";

    case 12:
      return "Once a year";

    case 24:
      return "Every 2 years";

    case 36:
      return "Every 3 years";

    case 48:
      return "Every 4 years";

    case 60:
      return "Every 5 years";
  }
};

export const EMPTY_NEW_KEY_CONTACT: ClientsKeyContactDetail = {
  id: 0,
  isContentEditable: true,
  isRelationshipReviewActive: true,
  companyOwnerUserId: "",
  costCenterId: 0,
  contact: null,
  primaryRelationshipStewardId: null,
  relationshipGrowthPlan: null,
  advocacyLevel: null,
  confidenceHistory: false,
  stakeholderConfidence: null,
};

export const getEmptyNewKeyContactForm = (
  today: Date,
  activeUserId: string
) => {
  return {
    primaryRelationshipStewardId: activeUserId,
    behaviorsAndConcerns: "",
    relationshipGrowthPlan: "",
    relationshipLastVerified: today,
    advocacyLevel: 0,
    advocacyTrending: 3,
    advocacyLastVerified: today,
    stakeholderConfidence: 0,
    stakeholderClientComment: "",
    stakeholderLastVerified: today,
    stakeholderLastVerifiedBy: activeUserId,
  };
};

export const loadKeyContactForm = (
  actualKeyContactData: ClientsKeyContactDetail,
  today: Date,
  activeUserId: string
) => {
  const advocacyLevel = actualKeyContactData.advocacyLevel;
  let advocacyLevelValue = 0;
  if (advocacyLevel) {
    advocacyLevelValue = advocacyLevel.level === 5 ? 0 : advocacyLevel.level;
  }
  const advocacyTrendingValue = advocacyLevel?.relationshipLevelTrendId ?? 3;
  const advocacyLastVerifiedValue = advocacyLevel?.lastVerified
    ? new Date(advocacyLevel.lastVerified)
    : today;
  const stakeholderConfidence = actualKeyContactData.stakeholderConfidence;
  const stakeholderConfidenceValue =
    stakeholderConfidence?.confidenceValue ?? 0;
  const stakeholderClientCommentValue =
    stakeholderConfidence?.clientComment ?? "";
  const stakeholderLastVerifiedValue = stakeholderConfidence?.lastVerified
    ? new Date(stakeholderConfidence.lastVerified)
    : today;
  const stakeholderLastVerifiedByValue =
    stakeholderConfidence?.lastVerifiedBy || activeUserId;
  const primaryRelationshipStewardIdValue =
    actualKeyContactData.primaryRelationshipStewardId || activeUserId;
  const behaviorsAndConcernsValue =
    actualKeyContactData.relationshipGrowthPlan?.behaviorsAndConcerns ?? "";
  const relationshipGrowthPlanValue =
    actualKeyContactData.relationshipGrowthPlan?.growthPlan ?? "";
  const relationshipLastVerifiedValue =
    actualKeyContactData.relationshipGrowthPlan?.lastVerified
      ? new Date(actualKeyContactData.relationshipGrowthPlan.lastVerified)
      : today;

  let response: ClientKeyContactDetailForm = {
    primaryRelationshipStewardId: primaryRelationshipStewardIdValue,
    behaviorsAndConcerns: behaviorsAndConcernsValue,
    relationshipGrowthPlan: relationshipGrowthPlanValue,
    relationshipLastVerified: relationshipLastVerifiedValue,
    advocacyLevel: advocacyLevelValue,
    advocacyTrending: advocacyTrendingValue,
    advocacyLastVerified: advocacyLastVerifiedValue,
    stakeholderConfidence: stakeholderConfidenceValue,
    stakeholderClientComment: stakeholderClientCommentValue,
    stakeholderLastVerified: stakeholderLastVerifiedValue,
    stakeholderLastVerifiedBy: stakeholderLastVerifiedByValue,
  };

  return response;
};

export const loadFirstStateStakeholderConfidence = (
  actualKeyContactData: ClientsKeyContactDetail,
  today: Date,
  activeUserId: string,
  isEditMode: boolean
) => {
  let response: StakeholderConfidence = isEditMode
    ? {
        confidenceValue: actualKeyContactData.stakeholderConfidence
          ? actualKeyContactData.stakeholderConfidence.confidenceValue
          : 0,
        clientComment: actualKeyContactData.stakeholderConfidence
          ? actualKeyContactData.stakeholderConfidence.clientComment
          : "",
        lastVerified: actualKeyContactData.stakeholderConfidence
          ? actualKeyContactData.stakeholderConfidence.lastVerified
          : today.toString(),
        lastVerifiedBy: actualKeyContactData.stakeholderConfidence
          ? actualKeyContactData.stakeholderConfidence.lastVerifiedBy
          : activeUserId ?? "",
      }
    : {
        confidenceValue: 0,
        clientComment: "",
        lastVerified: today.toString(),
        lastVerifiedBy: activeUserId ?? "",
      };
  return response;
};

export const loadFirstStateAdvocacyLevel = (
  actualKeyContactData: ClientsKeyContactDetail,
  today: Date,
  isEditMode: boolean
) => {
  let response: AdvocacyLevel = isEditMode
    ? {
        level: actualKeyContactData.advocacyLevel
          ? actualKeyContactData.advocacyLevel.level
          : 0,
        relationshipLevelTrendId: actualKeyContactData.advocacyLevel
          ? actualKeyContactData.advocacyLevel.relationshipLevelTrendId
          : 3,
        lastVerified: actualKeyContactData.advocacyLevel
          ? actualKeyContactData.advocacyLevel.lastVerified
          : today.toString(),
      }
    : {
        level: 0,
        relationshipLevelTrendId: 3,
        lastVerified: today.toString(),
      };
  return response;
};

export const checkContactIsInClientList = (
  list: Client[],
  clientId: number
) => {
  let ids: number[] = list.map((elem) => elem.id);
  return ids.includes(clientId);
};

export const checkClientCreatedByCostCenter = (companyName: string) => {
  return companyName.startsWith("ITX - ");
};

export const checkTrimForEdit = (value: string | null) => {
  return value ? value.trim() : null;
};

export const getOptionValue = <T,>(
  option: { value: T } | null | undefined
): T | null => {
  return option ? option.value : null;
};

export const getOptionValues = <T,>(
  options: { value: T }[] | null | undefined
): T[] | null => {
  return options ? options.map((option) => option.value) : null;
};

export const checkCouldDeactivate = (
  clientRecurringBillings: ClientRecurringBillingItem[],
  clientWorkOrders: ClientWorkOrderItem[],
  clientProjects: ClientProjectItem[]
) => {
  let response =
    clientRecurringBillings.some((rb) => rb.active) ||
    clientWorkOrders.some((wo) => wo.closedDate === null) ||
    clientProjects.some((p) => p.active);
  return !response;
};